import { format, isValid } from 'date-fns'
import { DatabaseZap, Loader2, ShieldAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  PendingAuthorization,
  configAnalyticsClient
} from '@/api/config-analytics-client'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

const durationItems = [
  { title: '2 hours', value: 2 },
  { title: '4 hours', value: 4 },
  { title: '8 hours (a working day)', value: 8 },
  { title: '24 hours', value: 24 }
]

const formatTime = (iso: string) => {
  const date = new Date(iso)

  return isValid(date) ? format(date, 'HH:mm') : iso
}

/** Only ever navigate to an http(s) URL, whatever the API returns. */
const goTo = (url: string) => {
  const target = new URL(url)

  if (target.protocol === 'http:' || target.protocol === 'https:')
    window.location.assign(target.toString())
}

/**
 * Consent page of the config analytics MCP OAuth flow. The API sends the
 * browser here from /oauth/authorize; approving returns it to the MCP client
 * with a single-use code.
 */
export default function ConfigAnalyticsAuthorizePage() {
  const [params] = useSearchParams()
  const requestId = params.get('request') ?? ''

  const [request, setRequest] = useState<PendingAuthorization | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [purpose, setPurpose] = useState('')
  const [duration, setDuration] = useState(8)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<'approved' | 'denied' | null>(null)

  useEffect(() => {
    if (!requestId) {
      setError('This link has no connection request.')

      return
    }
    configAnalyticsClient
      .getAuthorization(requestId)
      .then(setRequest)
      .catch((e: Error) => setError(e.message))
  }, [requestId])

  const decide = async (decision: 'approve' | 'deny') => {
    setBusy(true)
    try {
      const { redirect_to } =
        decision === 'approve'
          ? await configAnalyticsClient.approve(requestId, {
              purpose: purpose.trim(),
              duration_hours: duration
            })
          : await configAnalyticsClient.deny(requestId)

      setDone(decision === 'approve' ? 'approved' : 'denied')
      goTo(redirect_to)
    } catch (e) {
      setError((e as Error).message)
      setBusy(false)
    }
  }

  return (
    <div className="space-y-6 p-6 max-w-xl">
      <h1 className="text-xl font-semibold text-primary flex items-center gap-2">
        <DatabaseZap className="text-primary" size={20} />
        Connect to config analytics
      </h1>

      {error && (
        <div className="rounded-md border border-destructive p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {!request && !error && (
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="animate-spin" size={14} /> Loading…
        </p>
      )}

      {done && (
        <p className="text-sm text-primary">
          {done === 'approved'
            ? 'Connected. You can close this tab and go back to your MCP client.'
            : 'Denied. The MCP client was told.'}
        </p>
      )}

      {request && !done && (
        <>
          <div className="rounded-md border border-border p-4 space-y-2">
            <p className="text-sm text-primary">
              <span className="font-semibold">{request.client_name}</span> wants
              read-only access to the configuration of all ingested customer
              orgs (journeys, automations, workflows, templates, products,
              integrations, …) in your name.
            </p>
            <p className="text-xs text-muted-foreground">
              Returns to {request.redirect_host} · requested{' '}
              {formatTime(request.created_at)} · expires{' '}
              {formatTime(request.expires_at)}
            </p>
            <p className="text-xs text-primary flex items-center gap-1">
              <ShieldAlert size={12} />
              Only approve a connection you started yourself, just now.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="purpose">Purpose</Label>
            <Textarea
              id="purpose"
              onChange={(event) => setPurpose(event.target.value)}
              placeholder="e.g. Find customer orgs whose automations send more than two emails per trigger, for the Blueprint review"
              value={purpose}
            />
            <p className="text-xs text-muted-foreground">
              At least 20 characters. Logged with every query.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Duration</Label>
            <Select
              onValueChange={(value) => setDuration(Number(value))}
              value={String(duration)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {durationItems.map((item) => (
                  <SelectItem key={item.value} value={String(item.value)}>
                    {item.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Button
              disabled={busy || purpose.trim().length < 20}
              onClick={() => void decide('approve')}
            >
              {busy && <Loader2 className="mr-2 animate-spin" size={14} />}
              Approve connection
            </Button>
            <Button
              disabled={busy}
              onClick={() => void decide('deny')}
              variant="outline"
            >
              Deny
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
